import { useAuth0 } from "@auth0/auth0-react";
import axios from "axios";
import { InternalCacheRequestConfig } from "axios-cache-interceptor";
import { useEffect } from "react";

import { EnvVar } from "@/env";
import { useLogout } from "@/hooks/useLogout";
import { isUnauthorizedError, withAxiosApiResponseInterceptors } from "@/lib/axios";
import { axiosApi } from "@/Utils";

export default function Auth0TokenInjector() {
  const { getAccessTokenSilently } = useAuth0();
  const { logout } = useLogout();

  useEffect(() => {
    const requestInterceptorId = axiosApi.interceptors.request.use(
      async (config: InternalCacheRequestConfig) => {
        const tokenVerbose = await getAccessTokenSilently({
          cacheMode: "on",
          authorizationParams: {
            audience: EnvVar.auth0Audience,
            scope: "profile email read:current_user",
          },
          detailedResponse: true,
        });

        config.headers!.Authorization = `Bearer ${tokenVerbose.id_token}`;

        return config;
      },
      (error: unknown) => {
        console.error("Error in request interceptor:", error);
        return Promise.reject(error);
      }
    );

    const responseInterceptorId = axiosApi.interceptors.response.use(
      (response) => response,
      async (error: unknown) => {
        if (!isUnauthorizedError(error)) {
          return Promise.reject(error);
        }

        try {
          const refreshTokenResponse = await getAccessTokenSilently({
            cacheMode: "off",
            authorizationParams: {
              audience: EnvVar.auth0Audience,
              scope: "profile email read:current_user",
            },
            detailedResponse: true,
          });

          if (error.config?.headers) {
            error.config.headers.Authorization = `Bearer ${refreshTokenResponse.id_token}`;
          }
          // use new axios instance to bypass all base config and interceptors
          const replayAxios = axios.create();
          withAxiosApiResponseInterceptors(replayAxios);
          return replayAxios(error.config as InternalCacheRequestConfig);
        } catch (error) {
          console.error("Token refresh failed:", error);
          logout();
          return Promise.reject(error);
        }
      }
    );

    return () => {
      axiosApi.interceptors.request.eject(requestInterceptorId);
      axiosApi.interceptors.response.eject(responseInterceptorId);
    };
  }, [getAccessTokenSilently, logout]);

  return <></>;
}
